import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  RefreshCw, Save, Eye, EyeOff, HelpCircle, Plus, Settings2, 
  ArrowLeftRight, Truck
} from "lucide-react";

// Trendyol Marketplace Component
export function TrendyolSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    // API Ayarları
    saticiId: credentials?.seller_id || "",
    apiKey: credentials?.api_key || "",
    apiSecret: credentials?.api_secret || "",
    // Aktarım Ayarları
    gondericiAdresi: "",
    iadeAdresi: "",
    // Ürün Aktarım Ayarları
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    satisFiyatTip: "uye_tipi_fiyat_2",
    indirimlifiyatTip: "uye_tipi_fiyat_2",
    fiyatGonderimi: true,
    komisyonTuru: "hicbiri",
    komisyonDegeri: 0,
    tlFiyataKomisyonEkle: false,
    barkodaGoreAktar: true,
    dil: "tr",
    rezervStok: 0,
    modelKodu: "stok_kodu",
    marketplaceGrup: "marketplace_grup_1",
    komisyonuUrundenAl: false,
    rezervStokuUrundenAl: false,
    aktifPasifUrunleriAktar: false,
    // Sipariş Aktarım Ayarları
    siparisDurumu: ["olusturuldu"],
    siparisBolmePaketIslemleri: false
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "trendyol",
        seller_id: config.saticiId,
        api_key: config.apiKey,
        api_secret: config.apiSecret,
        extra_config: config
      });
      toast.success("Trendyol ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-sm">trendyol</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-orange-600">Trendyol</h2>
            <p className="text-slate-500">Türkiye'nin lider moda ve yaşam platformu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            YENİ EŞLEŞTİRME YAP
          </Button>
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            OTOMATİK EŞLEŞTİRME
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı / Güncelleme</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          {/* API Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Satıcı ID *</Label>
                  <Input 
                    value={config.saticiId}
                    onChange={(e) => setConfig({...config, saticiId: e.target.value})}
                    placeholder="1078708"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <Input 
                    value={config.apiKey}
                    onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                    placeholder="gxOnaGwwM5S2NgpBR"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Secret *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.apiSecret}
                      onChange={(e) => setConfig({...config, apiSecret: e.target.value})}
                      placeholder="API secret"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          {/* Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gönderici Adresi</Label>
                  <Select value={config.gondericiAdresi} onValueChange={(v) => setConfig({...config, gondericiAdresi: v})}>
                    <SelectTrigger><SelectValue placeholder="Adres seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="755024_istanbul_basaksehir">755024 İstanbul Başakşehir</SelectItem>
                      <SelectItem value="755025_istanbul_basaksehir">755025 İstanbul Başakşehir</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>İade Adresi</Label>
                  <Select value={config.iadeAdresi} onValueChange={(v) => setConfig({...config, iadeAdresi: v})}>
                    <SelectTrigger><SelectValue placeholder="Adres seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="755024_istanbul_basaksehir">755024 İstanbul Başakşehir</SelectItem>
                      <SelectItem value="755025_istanbul_basaksehir">755025 İstanbul Başakşehir</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ürün Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {/* Kolon 1 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ürün Adı *</Label>
                    <Select value={config.urunAdi} onValueChange={(v) => setConfig({...config, urunAdi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_adi">Ürün Adı</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Satış Fiyat Tip *</Label>
                    <Select value={config.satisFiyatTip} onValueChange={(v) => setConfig({...config, satisFiyatTip: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uye_tipi_fiyat_1">Üye Tipi Fiyat 1</SelectItem>
                        <SelectItem value="uye_tipi_fiyat_2">Üye Tipi Fiyat 2</SelectItem>
                        <SelectItem value="satis_fiyati">Satış Fiyatı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Türü</Label>
                    <Select value={config.komisyonTuru} onValueChange={(v) => setConfig({...config, komisyonTuru: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hicbiri">Hiçbiri</SelectItem>
                        <SelectItem value="yuzde">Yüzde</SelectItem>
                        <SelectItem value="sabit">Sabit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-orange-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.barkodaGoreAktar}
                      onCheckedChange={(c) => setConfig({...config, barkodaGoreAktar: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-orange-500"
                    />
                    <Label className="text-white">Barkoda Göre Aktar</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Rezerv Stok</Label>
                    <Input 
                      type="number"
                      value={config.rezervStok}
                      onChange={(e) => setConfig({...config, rezervStok: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.aktifPasifUrunleriAktar}
                      onCheckedChange={(c) => setConfig({...config, aktifPasifUrunleriAktar: c})}
                    />
                    <Label>Aktif ve Pasif Ürünleri Aktar</Label>
                  </div>
                </div>

                {/* Kolon 2 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Açıklama *</Label>
                    <Select value={config.aciklama} onValueChange={(v) => setConfig({...config, aciklama: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aciklama">Açıklama</SelectItem>
                        <SelectItem value="kisa_aciklama">Kısa Açıklama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>İndirimli Fiyat Tip *</Label>
                    <Select value={config.indirimlifiyatTip} onValueChange={(v) => setConfig({...config, indirimlifiyatTip: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uye_tipi_fiyat_1">Üye Tipi Fiyat 1</SelectItem>
                        <SelectItem value="uye_tipi_fiyat_2">Üye Tipi Fiyat 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Değeri</Label>
                    <Input 
                      type="number"
                      value={config.komisyonDegeri}
                      onChange={(e) => setConfig({...config, komisyonDegeri: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dil *</Label>
                    <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">tr</SelectItem>
                        <SelectItem value="en">en</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Model Kodu *</Label>
                    <Select value={config.modelKodu} onValueChange={(v) => setConfig({...config, modelKodu: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                        <SelectItem value="barkod">Barkod</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Kolon 3 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Marka Bilgisi *</Label>
                    <Select value={config.markaBilgisi} onValueChange={(v) => setConfig({...config, markaBilgisi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marka">Marka</SelectItem>
                        <SelectItem value="uretici">Üretici</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-orange-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.fiyatGonderimi}
                      onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-orange-500"
                    />
                    <Label className="text-white">Fiyat Gönderimi</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.tlFiyataKomisyonEkle}
                      onCheckedChange={(c) => setConfig({...config, tlFiyataKomisyonEkle: c})}
                    />
                    <Label>TL Fiyata Komisyon Ekle</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Marketplace Grup</Label>
                    <Select value={config.marketplaceGrup} onValueChange={(v) => setConfig({...config, marketplaceGrup: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketplace_grup_1">Marketplace Grup 1</SelectItem>
                        <SelectItem value="marketplace_grup_2">Marketplace Grup 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.komisyonuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, komisyonuUrundenAl: c})}
                    />
                    <Label>Komisyonu Üründen Al</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.rezervStokuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, rezervStokuUrundenAl: c})}
                    />
                    <Label>Rezerv Stoğu Üründen Al</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sipariş Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sipariş Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sipariş Durumu *</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-orange-100 text-orange-800">Oluşturuldu x</Badge>
                    <Badge variant="outline">+4</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 border rounded-lg">
                  <Checkbox 
                    checked={config.siparisBolmePaketIslemleri}
                    onCheckedChange={(c) => setConfig({...config, siparisBolmePaketIslemleri: c})}
                  />
                  <Label>Sipariş Bölme ve Paket İşlemleri</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım ve güncelleme özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Çiçeksepeti Marketplace Component
export function CiceksepetiSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    // API Ayarları
    supplierId: credentials?.seller_id || "",
    apiKey: credentials?.api_key || "",
    // Ürün Aktarım Ayarları
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    satisFiyatTipi: "uye_tipi_fiyat_4",
    indirimlifiyatTipi: "uye_tipi_fiyat_4",
    urunKoduTipi: "urun_id",
    modelKodu: "urun_kart_id",
    komisyonTuru: "hicbiri",
    komisyonDegeri: 0,
    rezervStok: 0,
    marketplaceGrup: "marketplace_grup_3",
    dil: "tr",
    komisyonuUrundenAl: false,
    rezervStokuUrundenAl: false,
    fiyatGonderimi: true,
    barkodaGoreAktar: true,
    barkodOnEki: "",
    aktifPasifUrunleriAktar: false,
    tlFiyataKomisyonEkle: false,
    // Sipariş Aktarım Ayarları
    siparisDurumu: ["toplama"],
    siparisKargoSurecleri: ""
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "ciceksepeti",
        seller_id: config.supplierId,
        api_key: config.apiKey,
        extra_config: config
      });
      toast.success("Çiçeksepeti ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-red-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            ÇS
          </div>
          <div>
            <h2 className="text-2xl font-bold text-pink-600">Çiçek Sepeti</h2>
            <p className="text-slate-500">Türkiye'nin çiçek ve hediye platformu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            YENİ EŞLEŞTİRME YAP
          </Button>
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            OTOMATİK EŞLEŞTİRME
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı / Güncelleme</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          {/* API Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier ID</Label>
                  <Input 
                    value={config.supplierId}
                    onChange={(e) => setConfig({...config, supplierId: e.target.value})}
                    placeholder="1500046355"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.apiKey}
                      onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                      placeholder="API key"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-pink-500 hover:bg-pink-600">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          {/* Ürün Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {/* Kolon 1 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ürün Adı</Label>
                    <Select value={config.urunAdi} onValueChange={(v) => setConfig({...config, urunAdi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_adi">Ürün Adı</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Satış Fiyat Tipi *</Label>
                    <Select value={config.satisFiyatTipi} onValueChange={(v) => setConfig({...config, satisFiyatTipi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uye_tipi_fiyat_1">Üye Tipi Fiyat 1</SelectItem>
                        <SelectItem value="uye_tipi_fiyat_4">Üye Tipi Fiyat 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Türü</Label>
                    <Select value={config.komisyonTuru} onValueChange={(v) => setConfig({...config, komisyonTuru: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hicbiri">Hiçbiri</SelectItem>
                        <SelectItem value="yuzde">Yüzde</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Marketplace Grup</Label>
                    <Select value={config.marketplaceGrup} onValueChange={(v) => setConfig({...config, marketplaceGrup: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketplace_grup_1">Marketplace Grup 1</SelectItem>
                        <SelectItem value="marketplace_grup_2">Marketplace Grup 2</SelectItem>
                        <SelectItem value="marketplace_grup_3">Marketplace Grup 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-pink-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.barkodaGoreAktar}
                      onCheckedChange={(c) => setConfig({...config, barkodaGoreAktar: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-pink-500"
                    />
                    <Label className="text-white">Barkoda Göre Aktar</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.tlFiyataKomisyonEkle}
                      onCheckedChange={(c) => setConfig({...config, tlFiyataKomisyonEkle: c})}
                    />
                    <Label>TL Fiyata Komisyon Ekle</Label>
                  </div>
                </div>

                {/* Kolon 2 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Açıklama</Label>
                    <Select value={config.aciklama} onValueChange={(v) => setConfig({...config, aciklama: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aciklama">Açıklama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>İndirimli Fiyat Tipi</Label>
                    <Select value={config.indirimlifiyatTipi} onValueChange={(v) => setConfig({...config, indirimlifiyatTipi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uye_tipi_fiyat_4">Üye Tipi Fiyat 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Değeri</Label>
                    <Input 
                      type="number"
                      value={config.komisyonDegeri}
                      onChange={(e) => setConfig({...config, komisyonDegeri: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dil *</Label>
                    <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">tr</SelectItem>
                        <SelectItem value="en">en</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Barkod Ön Eki</Label>
                    <Input 
                      value={config.barkodOnEki}
                      onChange={(e) => setConfig({...config, barkodOnEki: e.target.value})}
                    />
                  </div>
                </div>

                {/* Kolon 3 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Marka Bilgisi</Label>
                    <Select value={config.markaBilgisi} onValueChange={(v) => setConfig({...config, markaBilgisi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marka">Marka</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ürün Kodu Tipi *</Label>
                    <Select value={config.urunKoduTipi} onValueChange={(v) => setConfig({...config, urunKoduTipi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_id">Ürün ID</SelectItem>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rezerv Stok</Label>
                    <Input 
                      type="number"
                      value={config.rezervStok}
                      onChange={(e) => setConfig({...config, rezervStok: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.komisyonuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, komisyonuUrundenAl: c})}
                    />
                    <Label>Komisyonu Üründen Al</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.aktifPasifUrunleriAktar}
                      onCheckedChange={(c) => setConfig({...config, aktifPasifUrunleriAktar: c})}
                    />
                    <Label>Aktif ve Pasif Ürünleri Aktar</Label>
                  </div>
                </div>

                {/* Kolon 4 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Model Kodu *</Label>
                    <Select value={config.modelKodu} onValueChange={(v) => setConfig({...config, modelKodu: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_kart_id">Ürün Kart ID</SelectItem>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.rezervStokuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, rezervStokuUrundenAl: c})}
                    />
                    <Label>Rezerv Stoğu Üründen Al</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-pink-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.fiyatGonderimi}
                      onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-pink-500"
                    />
                    <Label className="text-white">Fiyat Gönderimi</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sipariş Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sipariş Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sipariş Durumu *</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-pink-100 text-pink-800">Toplama x</Badge>
                    <Badge variant="outline">+3</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Sipariş Kargo Süreçleri</Label>
                  <Select value={config.siparisKargoSurecleri} onValueChange={(v) => setConfig({...config, siparisKargoSurecleri: v})}>
                    <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standart">Standart</SelectItem>
                      <SelectItem value="hizli">Hızlı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım ve güncelleme özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default { TrendyolSettings, CiceksepetiSettings };
